/**
 * Типы описания раскладки.
 *
 * Раскладка задаёт три вещи: какой символ даёт клавиша, каким пальцем её
 * нажимают и где она физически расположена. Последнее нужно, чтобы отличить
 * промах по соседней клавише от незнания символа (ТЗ FR-EN-02).
 */

import type {
  Finger,
  KeyboardForm,
  LanguageCode,
  LayoutId,
  OsPlatform,
} from '@type2x/shared-contracts';
import { handOfFinger } from '@type2x/shared-contracts';

/** Ряд физической клавиатуры. Нумерация сверху вниз. */
export const KEY_ROWS = ['number', 'top', 'home', 'bottom', 'space'] as const;
export type KeyRow = (typeof KEY_ROWS)[number];

/**
 * Пара одноимённых пальцев обеих рук.
 *
 * Учебные режимы открывают клавиши парами пальцев: движение указательного
 * левой руки — то же движение, что у указательного правой, и учить их порознь
 * значит учить дважды. Большие пальцы в пару не входят: у них одна клавиша.
 */
export const FINGER_PAIRS = ['index', 'middle', 'ring', 'pinky'] as const;
export type FingerPair = (typeof FINGER_PAIRS)[number];

/** Пара пальцев, к которой относится палец; null — большой палец. */
export function fingerPairOf(finger: Finger): FingerPair | null {
  const name = finger.replace(`${handOfFinger(finger)}-`, '');
  return (FINGER_PAIRS as readonly string[]).includes(name) ? (name as FingerPair) : null;
}

/** Одна физическая клавиша раскладки. */
export interface KeyDefinition {
  /** Символ без Shift. */
  readonly base: string;
  /** Символ с Shift. Пустая строка, если клавиша не даёт второго символа. */
  readonly shifted: string;
  readonly finger: Finger;
  readonly row: KeyRow;
  /** Позиция клавиши в ряду, с нуля. */
  readonly column: number;
  /** Формы клавиатуры, на которых клавиша присутствует. */
  readonly forms: readonly KeyboardForm[];
}

/**
 * Отличие одной клавиши на конкретной операционной системе.
 *
 * Клавиша задаётся местом — рядом и колонкой, — а не символом: меняется как
 * раз то, какой символ она даёт, и искать её по символу было бы поиском по
 * тому, чего ещё нет. Палец и место при этом остаются: физическая клавиатура
 * от операционной системы не зависит.
 */
export interface KeyOverride {
  readonly row: KeyRow;
  /** Позиция клавиши в ряду, с нуля. */
  readonly column: number;
  readonly base: string;
  readonly shifted: string;
}

/** Полное описание раскладки. */
export interface KeyboardLayout {
  readonly id: LayoutId;
  readonly language: LanguageCode;
  /** Клавиши домашнего ряда, на которые ставятся пальцы. */
  readonly homeRowAnchors: readonly string[];
  readonly keys: readonly KeyDefinition[];
  /**
   * Операционная система, для которой описана раскладка.
   *
   * Одна и та же раскладка на разных системах расставляет часть символов
   * по-разному (ТЗ FR-EN-42), и подсказка клавиши без этого была бы верной
   * только для одной из них.
   */
  readonly platform: OsPlatform;
}

/** Отличия раскладки по операционным системам. */
export type PlatformOverrides = Readonly<Partial<Record<OsPlatform, readonly KeyOverride[]>>>;

/** Как ввести конкретный символ. */
export interface KeyStroke {
  readonly key: KeyDefinition;
  /** Требуется ли Shift для получения символа. */
  readonly requiresShift: boolean;
}
